import React from "react";
import { InputValidationRegExps } from "../../projects/enumerators";
import { CalculationMethodEnum, ServicingCountTypeEnum } from "../../templates/templateModels";

interface TemplateDetailsData {
  servicingAnalysisType: string;
  categories: CategoryModel[];
  servicingAnalysisGranularity: string;
  accessCriteriaSpecificationGranularity: string;
  accessCodeMapSetId: number;
  accessCodeMapSetVersionId: number;
  closestProviderCount: number;
  correlationId: string;
  memberSetId: number;
  zipClassType: string;
  attestationCrosswalkCodeMapSetId: number;
  useMedicareRatebook: boolean;
  templateId: string;
  templateCollectionId: number;
  templateDescription: string;
  displayName: string;
  timeDistanceCalculatorTypes: string[];
  skipServicingAnalysis: boolean;
  motiveCrosswalkCodeMapSetId: number;
  createdByUserId: number;
  creationComment: string;
}

interface CategoryModel {
  displayOrder: number;
  displayName: string;
  specialtyGroups: SpecialtyGroupModel[];
}

interface SpecialtyGroupModel {
  categoryDisplayOrder?: number;
  categoryName?: string;
  code: string;
  displayOrder: number;
  name: string;
  entityType: string;
  specialties: SpecialtyModel[];
  accessCriteria: AccessCriteriaModel[];
  analysisProperties: string;
  displayProperties: string;
  servicingCountBy: ServicingCountTypeEnum;
  servicingCriteria: ServicingCriteriaModel[];
  servicingMinimumType: string;
  isNameValid: boolean;
  isSelected: boolean;
  isValid: boolean;
  enabled: boolean;
  taxonomyCodesCount: number | undefined;
}

interface SpecialtyModel {
  code: string;
  name: string;
  displayOrder: number;
  weight: string;
}

interface AccessCriteriaModel {
  calculationMethod: CalculationMethodEnum;
  geographyId: string;
  maximumDistanceAllowed: number;
  maximumTimeAllowed: number;
  minimumProviderCount: number;
  passPercentage: number;
  isAccessCustomization: boolean;
  telehealthAccessCreditPercentage: number;
  isTelehealthAccessCreditCustomization: boolean;
  servicingCountMethodology: string;
  conAccessCreditPercentage: number;
  cmsApplicationCreditPercentage: number;
  providerRegionType: string;
  skipAccessTimeAndDistanceCalculations: boolean;
  isValid: boolean;
}

interface ServicingCriteriaModel {
  geographyId: string;
  servicingMinimumCount: number;
  servicingRatio: string;
  servicingAnalysisType: string;
}

interface AddTemplateDetailsData {
  servicingAnalysisType: string;
  categories: CategoryModel[];
  servicingAnalysisGranularity: string;
  accessCriteriaSpecificationGranularity: string;
  accessCodeMapSetId: number;
  accessCodeMapSetVersionId: number;
  closestProviderCount: number;
  correlationId: string;
  memberSetId: number;
  zipClassType: string;
  attestationCrosswalkCodeMapSetId: number;
  useMedicareRatebook: boolean;
  templateId: string;
  templateCollectionId: number;
  templateDescription: string;
  displayName: string;
  timeDistanceCalculatorTypes: string[];
  skipServicingAnalysis: boolean;
  motiveCrosswalkCodeMapSetId: number;
  createdByUserId: number;
  creationComment: string;
}

function processKeyDownNumericValidation(e: React.KeyboardEvent<HTMLInputElement>, regExpString: InputValidationRegExps, allowDecimalPoint: boolean = false): boolean {
  if (e.key == 'Enter' || e.key == "Tab" || e.key == "Backspace" || e.key == "Delete" || e.key == "ArrowLeft" || e.key == "ArrowRight" || (e.key == "c" && e.ctrlKey) || (e.key == "v" && e.ctrlKey) || (e.key == "x" && e.ctrlKey)) {
    return true; //allow these keys to pass thru so that the user can navigate the table
  }

  let keyIsValid: boolean = false;

  if (allowDecimalPoint === true && e.key == ".") {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    keyIsValid = true;
  }

  if (keyIsValid === false) {
    const regNumberOnly: RegExp = new RegExp(InputValidationRegExps.regexpOnlyAllowDigit);

    const regNumberOnlyResult = regNumberOnly.exec(e.key);

    if (regNumberOnlyResult?.length) {
      keyIsValid = true;
    }
  }

  if (keyIsValid === true) {
    let value = e.currentTarget.value;
    const selectedText = value.slice(e.currentTarget.selectionStart, e.currentTarget.selectionEnd);
    value = value.replace(selectedText, "").replace("NaN", "");

    const valueToVerify = value + e.key;
    const regexToExecute = new RegExp(regExpString);
    const regexResult = regexToExecute.exec(valueToVerify);
    if (regexResult?.length > 0) {
      return true;
    } else {
      e.preventDefault(); //prevents the key from going thru to the UI
      return false;
    }
  } else {
    e.preventDefault();
    return false;
  }
}

function processKeyDownTextValidation(e: React.KeyboardEvent<HTMLInputElement>, regExpString: InputValidationRegExps): boolean {
  if (e.key == 'Enter' || e.key == "Tab" || e.key == "Backspace" || e.key == "Delete" || e.key == "ArrowLeft" || e.key == "ArrowRight" || (e.key == "c" && e.ctrlKey) || (e.key == "v" && e.ctrlKey) || (e.key == "x" && e.ctrlKey)) {
    return true; //allow these keys to pass thru so that the user can navigate the table
  }

  const regexToExecute = new RegExp(regExpString);
  const regNumberOnlyResult = regexToExecute.exec(e.key);

  if (regNumberOnlyResult?.length) {
    return true;
  } else {
    e.preventDefault();
    return false;
  }
}

enum EntityTypeEnum {
  Facility = "facility",
  Provider = "provider"
}

enum AdequacySpecialtyGroupAnalysisPropertiesEnum {
  None = "none",
  InformationalOnly = "informationalonly"
}

enum AdequacySpecialtyGroupDisplayPropertiesEnum  {
    None = "none",
    SuppressSpecialtyAccessResults = "suppressspecialtyaccessresults",
    CanExpandSpecialties = "canexpandspecialties",
    SuppressInMemberReports = "suppressinmemberreports"
}

export {
  TemplateDetailsData,
  CategoryModel,
  SpecialtyGroupModel,
  SpecialtyModel,
  AccessCriteriaModel,
  ServicingCriteriaModel,
  AddTemplateDetailsData,
  processKeyDownNumericValidation,
  processKeyDownTextValidation,
  EntityTypeEnum,
  AdequacySpecialtyGroupAnalysisPropertiesEnum,
  AdequacySpecialtyGroupDisplayPropertiesEnum,
}